import { useEffect, useState } from "react";
import type { ProcedureCode } from "@dentacore/shared-types";

import { listProcedureCodes } from "../api/catalogue";
import { addVisitWork } from "../api/patients";
import { formatMoney } from "./money";
import { ALL_PRIMARY_TEETH, ALL_TEETH, toothName } from "./tooth-geometry";
import styles from "./visit-dialog.module.css";

interface Line {
  procedureCodeId: string;
  /** Empty until a tooth is chosen; ignored for a whole-mouth procedure. */
  tooth: string;
}

const EMPTY: Line = { procedureCodeId: "", tooth: "" };

/**
 * **Add work done — more work on a finished visit, recorded and billed in one
 * action** (EB7, 2026-09-29).
 *
 * A root canal was completed and billed, and a polish was done too. The tester
 * added the polish with *Add a line* on the bill, so it reached Billing and
 * nothing else — the chart's *Record a procedure* was a second entry they had
 * no reason to know about. This writes both at once: the procedure (on the
 * chart when it has a tooth, in History either way) and **a new invoice** for
 * it, because the visit's own invoice was issued when it completed and an
 * issued invoice never changes.
 *
 * **The procedures are the active location's catalogue**, the one the top
 * bar has picked. A visit at another location is refused by the server with a
 * sentence rather than priced from the wrong list.
 */
export function AddWorkForm({
  patientId,
  appointmentId,
  currency,
  onDone,
  onCancel,
}: {
  patientId: string;
  appointmentId: string;
  currency: string;
  /** The work is recorded and billed; the dialog re-reads the visit. */
  onDone: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  const [codes, setCodes] = useState<readonly ProcedureCode[] | null>(null);
  const [lines, setLines] = useState<Line[]>([EMPTY]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    listProcedureCodes(false, controller.signal)
      .then((rows) => setCodes(rows.filter((row) => row.isActive)))
      .catch(() => {
        if (!controller.signal.aborted) setError("Could not read this location's procedures.");
      });
    return () => controller.abort();
  }, []);

  const codeOf = (id: string): ProcedureCode | undefined => codes?.find((code) => code.id === id);

  const perTooth = (line: Line): boolean => codeOf(line.procedureCodeId)?.scope === "PER_TOOTH";

  /** Every line has a procedure, and a tooth where the procedure needs one. */
  const ready =
    lines.length > 0 &&
    lines.every((line) => line.procedureCodeId !== "" && (!perTooth(line) || line.tooth !== ""));

  const totalCents = lines.reduce(
    (sum, line) => sum + (codeOf(line.procedureCodeId)?.defaultPriceCents ?? 0),
    0,
  );

  const change = (index: number, next: Partial<Line>): void =>
    setLines((was) => was.map((line, at) => (at === index ? { ...line, ...next } : line)));

  function submit(): void {
    setBusy(true);
    setError(null);
    addVisitWork(patientId, {
      appointmentId,
      items: lines.map((line) =>
        perTooth(line)
          ? { procedureCodeId: line.procedureCodeId, fdiNumber: Number(line.tooth) }
          : { procedureCodeId: line.procedureCodeId },
      ),
    })
      .then(onDone)
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not add that work.");
      })
      .finally(() => setBusy(false));
  }

  return (
    <div className={styles.addWork} data-testid="add-work-form">
      <h4 className={styles.addWorkTitle}>Add work done at this visit</h4>

      {codes === null ? (
        <p className={styles.hint}>Reading&hellip;</p>
      ) : codes.length === 0 ? (
        <p className={styles.hint}>This location has no procedures yet.</p>
      ) : (
        <>
          {lines.map((line, index) => (
            <div key={index} className={styles.addWorkLine} data-testid="add-work-line">
              <select
                className={styles.select}
                value={line.procedureCodeId}
                disabled={busy}
                aria-label="Procedure"
                onChange={(event) => change(index, { procedureCodeId: event.target.value })}
                data-testid="add-work-procedure"
              >
                <option value="">Choose a procedure</option>
                {codes.map((code) => (
                  <option key={code.id} value={code.id}>
                    {code.label} · {formatMoney(code.defaultPriceCents, currency)}
                  </option>
                ))}
              </select>

              {perTooth(line) ? (
                <select
                  className={styles.select}
                  value={line.tooth}
                  disabled={busy}
                  aria-label="Tooth"
                  onChange={(event) => change(index, { tooth: event.target.value })}
                  data-testid="add-work-tooth"
                >
                  <option value="">Choose a tooth</option>
                  {/* Baby teeth after the adult ones (BT4); the API refuses a held-back tooth. */}
                  {[...ALL_TEETH, ...ALL_PRIMARY_TEETH].map((fdi) => (
                    <option key={fdi} value={String(fdi)}>
                      #{fdi} {toothName(fdi)}
                    </option>
                  ))}
                </select>
              ) : line.procedureCodeId !== "" ? (
                <span className={styles.hint}>Whole mouth</span>
              ) : null}

              {lines.length > 1 && (
                <button
                  type="button"
                  className={styles.secondary}
                  disabled={busy}
                  onClick={() => setLines((was) => was.filter((_, at) => at !== index))}
                  aria-label="Remove this procedure"
                >
                  ×
                </button>
              )}
            </div>
          ))}

          <div className={styles.modalActions}>
            <button
              type="button"
              className={styles.secondary}
              disabled={busy || lines.length >= 20}
              onClick={() => setLines((was) => [...was, EMPTY])}
              data-testid="add-work-another"
            >
              Add another
            </button>
            <button
              type="button"
              className={styles.primary}
              disabled={busy || !ready}
              onClick={submit}
              data-testid="add-work-save"
            >
              Record and bill {formatMoney(totalCents, currency)}
            </button>
            <button type="button" className={styles.secondary} disabled={busy} onClick={onCancel}>
              Cancel
            </button>
            <span className={styles.hint}>
              Recorded on the chart and in History, and billed on a new invoice for this visit.
            </span>
          </div>
        </>
      )}

      {error !== null && (
        <p className={styles.error} data-testid="add-work-error">
          {error}
        </p>
      )}
    </div>
  );
}
